<template lang="pug">
el-drawer(
  title="編輯館別"
  :visible.sync="drawer"
  direction="rtl"
  :before-close="handleClose")
  .drawer.flex.column.gap-20
    .data.flex.column.gap-6
      .title 館別名稱
      .input.flex.align-center.justify-stretch
        el-input.input(disabled, v-model="name")
    .data.flex.column.gap-6
      .title 藍新商店 ID (merchant ID)
      .input.flex.align-center.justify-stretch
        el-input.input(v-model="merchantID", placeholder="請輸入藍新商店 ID", clearable)
      .hint(v-if="hasCredential") 清空商店 ID 並儲存，會清除此館別的藍新設定（改用預設金流帳號）
    .data.flex.column.gap-6
      .title 藍新 HASH_KEY (長度 32 字元)
      .input.flex.align-center.justify-stretch
        el-input.input(
          v-model="hashKey",
          :placeholder="hashKeySet ? '已設定（不顯示），留空則保留原值' : '請輸入藍新 HASH_KEY'",
          show-password,
          autocomplete="new-password")
      .hint.error(v-if="hashKeyError") {{ hashKeyError }}
    .data.flex.column.gap-6
      .title 藍新 HASH_IV (長度 16 字元)
      .input.flex.align-center.justify-stretch
        el-input.input(
          v-model="hashIV",
          :placeholder="hashIVSet ? '已設定（不顯示），留空則保留原值' : '請輸入藍新 HASH_IV'",
          show-password,
          autocomplete="new-password")
      .hint.error(v-if="hashIVError") {{ hashIVError }}
    .op.flex.align-center-justify-stretch.gap-10
      el-button.button(@click="cancel") 取消
      el-button.button(@click="updateBranch", type="primary") 設定
</template>

<script>
import {
  updateBranchNewebpay,
} from '@/api/place';

const HASH_KEY_LENGTH = 32;
const HASH_IV_LENGTH = 16;

// 後端錯誤碼對應的中文說明
const ERROR_MESSAGES = {
  invalid_hash_key_length: `HASH_KEY 長度必須為 ${HASH_KEY_LENGTH} 字元`,
  invalid_hash_iv_length: `HASH_IV 長度必須為 ${HASH_IV_LENGTH} 字元`,
  invalid_inputs: '商店 ID、HASH_KEY、HASH_IV 需全部填寫，或清空商店 ID 以清除設定',
};

export default {
  name: 'place-edit',
  data() {
    return {
      drawer: false,

      origData: undefined,
      name: '',
      merchantID: '',
      // 後端不再回傳 HASH_KEY／HASH_IV 原值，只回傳是否已設定；輸入框只放使用者新輸入的值
      hashIV: '',
      hashKey: '',
      hashKeySet: false,
      hashIVSet: false,
    };
  },
  computed: {
    hasCredential() {
      return Boolean(this.origData?.newebpay_merchant_id) || this.hashKeySet || this.hashIVSet;
    },
    hashKeyError() {
      const v = this.hashKey.trim();
      if (v && v.length !== HASH_KEY_LENGTH) {
        return `HASH_KEY 長度必須為 ${HASH_KEY_LENGTH} 字元（目前 ${v.length} 字元）`;
      }
      return '';
    },
    hashIVError() {
      const v = this.hashIV.trim();
      if (v && v.length !== HASH_IV_LENGTH) {
        return `HASH_IV 長度必須為 ${HASH_IV_LENGTH} 字元（目前 ${v.length} 字元）`;
      }
      return '';
    },
  },
  methods: {
    handleClose(done) {
      done();
    },
    show() {
      this.drawer = true;
    },
    reset() {
      this.origData = undefined;
      this.name = '';
      this.merchantID = '';
      this.hashIV = '';
      this.hashKey = '';
      this.hashKeySet = false;
      this.hashIVSet = false;
    },
    close(result) {
      this.$emit('finish', result);
      this.drawer = false;
      this.reset();
    },
    cancel() {
      this.close(false);
    },
    // 回傳錯誤訊息；通過檢查回傳空字串
    validate() {
      const merchantID = this.merchantID.trim();
      const hashKey = this.hashKey.trim();
      const hashIV = this.hashIV.trim();

      if (this.hashKeyError) return this.hashKeyError;
      if (this.hashIVError) return this.hashIVError;

      if (!merchantID) {
        if (hashKey || hashIV) {
          return '請輸入藍新商店 ID';
        }
        return '';
      }
      if (!hashKey && !this.hashKeySet) {
        return '請輸入藍新 HASH_KEY';
      }
      if (!hashIV && !this.hashIVSet) {
        return '請輸入藍新 HASH_IV';
      }
      return '';
    },
    async updateBranch() {
      const err = this.validate();
      if (err) {
        this.$showWarn(err);
        return;
      }

      const merchantID = this.merchantID.trim();
      if (!merchantID) {
        if (!this.hasCredential) {
          // 原本就沒設定，也沒輸入任何值，不用送出
          this.close(false);
          return;
        }
        try {
          await this.$showConfirm({
            title: '清除藍新設定',
            content: '商店 ID 為空，儲存後會清除此館別的藍新設定並改用預設金流帳號，確定嗎？',
            confirmButtonText: '確定清除',
            cancelButtonText: '取消',
            confirmType: 'warning',
          });
        } catch (e) {
          return;
        }
      }

      await this.$execWithLoading(async () => {
        await updateBranchNewebpay(this.origData, merchantID, this.hashKey.trim(), this.hashIV.trim());
        this.$showSuccess('設定更新');
        this.close(true);
      }, (e) => {
        const reason = ERROR_MESSAGES[e?.response?.data?.error];
        if (reason) {
          this.$showError(`設定失敗：${reason}`);
        } else {
          this.$showAxiosException('設定失敗', e);
        }
      });
    },
    loadBranch(data) {
      this.origData = data;
      this.name = data.name;

      this.merchantID = data.newebpay_merchant_id || '';
      this.hashKeySet = Boolean(data.newebpay_hash_key_set);
      this.hashIVSet = Boolean(data.newebpay_hash_iv_set);
    },
  },
  mounted() {
    this.$on('show', (data) => {
      this.reset();
      this.loadBranch(data);
      this.show();
    });
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.drawer {
  padding: 0 20px;
  padding-bottom: 20px;
  .hint {
    font-size: 12px;
    color: $text-secondary-color;
    &.error {
      color: $danger-color;
    }
  }
  .op {
    .button {
      flex: 1;
    }
  }
  .photo {
    input[type=file] {
      display: none;
    }
    .preview {
      .remove-icon {
        color: $danger-color;
        width: 13px;
        height: 13px;
        border-radius: 100%;
        border: 1px solid $danger-color;
        i {
          zoom: 0.6;
        }
      }
      img {
        &.logo {
          width: 300px;
          height: 48px;
        }
        &.seal {
          width: 240px;
          height: 200px;
        }
        &.position {
          width: 240px;
        }
      }
    }
  }
}
</style>
